import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    IconButton,
    Alert,
    Stack,
    Grid,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import GppGoodIcon from '@mui/icons-material/GppGood';
import SchoolIcon from '@mui/icons-material/School';
import axiosInstance from "../../axiosInstance";
import useDictation from '../../hooks/useDictation';
import { initializeDB } from '../../config/db';
import {
    SSD_STORE,
    EMPLOYEE_STORE,
    STATUS_PROCESS,
    STATUS_FINISH,
    STATUS_SEND
} from '../../config/constants';
import { getTodayDate } from '../../utils/dateUtils';
import { generateUUID } from '../../utils/uuid';
import SignatureModalWithAuto from '../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';

// Тема больше не обрезается при сохранении — в базе LONGTEXT, в PDF уходит целиком.
// Но в тему письма, SMS и имя PDF-файла она попадает выжимкой, поэтому после этой
// отметки честно предупреждаем: длинный текст никуда не денется, но в коротких
// полях будет укорочен.
const TOPIC_LONG_AT = 2000;

export default function SSDDetail() {
    const navigate = useNavigate();
    const [ssdId, setSsdId] = useState(null);
    const [type, setType] = useState('safety_stand_down');
    const [topic, setTopic] = useState('');
    const [participants, setParticipants] = useState([]);
    const [givenBy, setGivenBy] = useState(null); // { FullName, _type, employeeId }
    const [date, setDate] = useState(getTodayDate());
    const [status, setStatus] = useState(STATUS_PROCESS);
    const [uuid, setUuid] = useState(null);
    const sendBy = localStorage.getItem('employeeId');

    const [openSelectModal, setOpenSelectModal] = useState(false);
    const [openGivenByModal, setOpenGivenByModal] = useState(false);
    const [openSignModal, setOpenSignModal] = useState(false);
    const sigPadRef = useRef(null);
    const [selectedParticipantIndex, setSelectedParticipantIndex] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState('');
    const didInitRef = useRef(false);

    // Dictation for topic field
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
    });

    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        (async () => {
            try {
                const db = await initializeDB();
                const readTx = db.transaction(SSD_STORE, 'readonly');
                const ssdStore = readTx.objectStore(SSD_STORE);
                const allRecords = await ssdStore.getAll();
                await readTx.done;
                let record = allRecords.find(r => r.status === STATUS_PROCESS);
                if (!record) {
                    const newRecord = {
                        type: 'safety_stand_down',
                        topic: '',
                        employees: [],
                        givenBy: null,
                        date: getTodayDate(),
                        status: STATUS_PROCESS,
                        uuid: generateUUID()
                    };
                    const writeTx = db.transaction(SSD_STORE, 'readwrite');
                    const wStore = writeTx.objectStore(SSD_STORE);
                    const newKey = await wStore.add(newRecord);
                    await writeTx.done;
                    newRecord.id = newKey;
                    record = newRecord;
                }

                // Auto-fill givenBy from localStorage if empty
                if (!record.givenBy) {
                    const empIdFromStorage = localStorage.getItem('employeeId');
                    const firstNameFromStorage = localStorage.getItem('firstName') || '';
                    const lastNameFromStorage = localStorage.getItem('lastName') || '';
                    if (empIdFromStorage && (firstNameFromStorage || lastNameFromStorage)) {
                        const fullName = `${firstNameFromStorage} ${lastNameFromStorage}`.trim();
                        record.givenBy = {
                            FullName: fullName,
                            fullName: fullName, // lowercase variant
                            _type: 'employee',
                            employeeId: empIdFromStorage,
                            ee: empIdFromStorage, // Add ee field for compatibility
                            EE: empIdFromStorage  // Add EE field for compatibility
                        };
                        const writeTx2 = db.transaction(SSD_STORE, 'readwrite');
                        const wStore2 = writeTx2.objectStore(SSD_STORE);
                        await wStore2.put(record);
                        await writeTx2.done;
                    }
                }

                // Generate UUID if missing in old records
                if (!record.uuid) {
                    record.uuid = generateUUID();
                    const writeTx3 = db.transaction(SSD_STORE, 'readwrite');
                    const wStore3 = writeTx3.objectStore(SSD_STORE);
                    await wStore3.put(record);
                    await writeTx3.done;
                }

                setSsdId(record.id);
                setType(record.type || 'safety_stand_down');
                setTopic(record.topic || '');
                setParticipants(record.employees || []);
                setGivenBy(record.givenBy || null);
                setDate(record.date || getTodayDate());
                setStatus(record.status || STATUS_PROCESS);
                setUuid(record.uuid);
            } catch (error) {
                console.error('Error init SSD record:', error);
                setErrorMsg('Error loading form');
            }
        })();
    }, []);


    async function saveRecordAndReload({
                                           newStatus = STATUS_PROCESS,
                                           newDate,
                                           newParticipants = null,
                                           newGivenBy = null,
                                           newType = null
                                       } = {}) {
        if (!ssdId) return;
        const finalParticipants = newParticipants ?? participants;
        const finalGivenBy = newGivenBy ?? givenBy;
        const finalType = newType ?? type;
        const recordToSave = {
            id: ssdId,
            type: finalType,
            topic,
            employees: finalParticipants,
            givenBy: finalGivenBy,
            date: newDate ?? date,
            status: newStatus,
            uuid
        };
        try {
            const db = await initializeDB();
            const writeTx = db.transaction(SSD_STORE, 'readwrite');
            const store = writeTx.objectStore(SSD_STORE);
            await store.put(recordToSave);
            await writeTx.done;
            setType(recordToSave.type);
            setTopic(recordToSave.topic);
            setParticipants(recordToSave.employees);
            setGivenBy(recordToSave.givenBy);
            setDate(recordToSave.date);
            setStatus(recordToSave.status);
        } catch (err) {
            console.error('Error saving SSD record:', err);
        }
    }

    const handleTopicChange = e => setTopic(e.target.value);
    const handleTopicBlur = () => {
        saveRecordAndReload({ newStatus: STATUS_PROCESS });
    };
    const handleDateChange = e => {
        const newDate = e.target.value;
        setDate(newDate);
        saveRecordAndReload({ newStatus: STATUS_PROCESS, newDate });
    };

    const startTopicDictation = async () => {
        await startDictation({
            onResult: (text) => {
                if (!text) return;
                const newTopic = topic ? topic + ' ' + text : text;
                setTopic(newTopic);
            },
            onEnd: () => {},
            onError: () => {},
            continuous: false,
            interimResults: false,
            safetyTimeoutMs: 15000,
            prime: true,
        });
    };

    const stopTopicDictation = () => {
        try { stopDictation(); } catch (e) { /* ignore */ }
        setTimeout(() => { try { stopDictation(); } catch (e) {} }, 150);
    };

    useEffect(() => {
        const kill = () => { try { stopDictation(); } catch (e) {} };
        window.addEventListener('pagehide', kill);
        document.addEventListener('visibilitychange', () => { if (document.hidden) kill(); });
        return () => {
            try { stopDictation(); } catch (e) {}
            window.removeEventListener('pagehide', kill);
        };
    }, [stopDictation]);

    const handleOpenSelectModal = () => setOpenSelectModal(true);

    const handleSaveParticipants = async selectedArr => {
        const newList = selectedArr.map(sel => {
            const existing = participants.find(
                p => p.FullName === sel.FullName && p._type === sel._type
            );
            return {
                FullName: sel.FullName,
                _type: sel._type,
                Division: sel.Division || '',
                Agency: sel.Agency || '',
                signature: existing?.signature || null,
                signedAt: existing?.signedAt || null
            };
        });
        setParticipants(newList);
        setOpenSelectModal(false);
        await saveRecordAndReload({ newStatus: STATUS_PROCESS, newParticipants: newList });
    };

    const handleDeleteParticipant = async idx => {
        const newArr = [...participants];
        newArr.splice(idx, 1);
        setParticipants(newArr);
        await saveRecordAndReload({ newStatus: STATUS_PROCESS, newParticipants: newArr });
    };

    const handleOpenSignModal = idx => {
        setSelectedParticipantIndex(idx);
        setOpenSignModal(true);
    };

    const handleAcceptSignature = async () => {
        if (!sigPadRef.current) return;
        const signatureURL = sigPadRef.current.toDataURL();
        const now = new Date().toISOString();
        setOpenSignModal(false);
        const updated = [...participants];
        updated[selectedParticipantIndex] = {
            ...updated[selectedParticipantIndex],
            signature: signatureURL,
            signedAt: now
        };
        setParticipants(updated);
        await saveRecordAndReload({ newStatus: STATUS_PROCESS, newParticipants: updated });
    };

    const handleSaveGivenBy = async (selected) => {
        if (!selected || selected.length === 0) {
            setOpenGivenByModal(false);
            return;
        }
        const person = selected[0]; // Take first
        const empId = person._type === 'employee' ? (person.ee || person.EE || '') : (person.agency || person.Agency || '');
        const newVal = {
            ...person, // Include all fields from selected person
            FullName: person.FullName || '',
            fullName: person.FullName || '', // lowercase variant
            _type: person._type || 'employee',
            employeeId: empId,
            ee: empId,
            EE: empId
        };
        setGivenBy(newVal);
        setOpenGivenByModal(false);
        await saveRecordAndReload({ newStatus: STATUS_PROCESS, newGivenBy: newVal });
    };

    const handleSend = async () => {
        if (!isFormValid()) {
            setErrorMsg('Please fill all required fields and ensure all participants have signed.');
            return;
        }
        setIsSending(true);
        setErrorMsg('');
        try {
            const payload = {
                type,
                topic,
                employees: participants,
                givenBy: { employeeId: givenBy?.employeeId || '' },
                date,
                sendBy,
                uuid
            };

            const resp = await axiosInstance.post('/safety-stand-down/save', payload);

            if (resp.status === 200) {
                // Проверяем если форма уже была отправлена ранее
                if (resp.data.status === 'already_submitted') {
                    // Очищаем форму и создаём новую запись с новым UUID
                    const db = await initializeDB();
                    const tx = db.transaction(SSD_STORE, 'readwrite');
                    const store = tx.objectStore(SSD_STORE);
                    await store.delete(ssdId);
                    await tx.done;

                    setSuccess('Form was already submitted. Starting new form...');
                    setTimeout(() => window.location.reload(), 1500);
                    return;
                }

                await saveRecordAndReload({ newStatus: STATUS_SEND });
                setSuccess('Form sent successfully!');
                setTimeout(() => navigate('/'), 1500);
            } else {
                setErrorMsg('Server returned error code: ' + resp.status);
            }
        } catch (err) {
            console.error('Error sending form:', err);
            setErrorMsg('Error sending form: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsSending(false);
        }
    };

    // topic прилетает из IndexedDB и на старых записях бывает undefined — не роняем рендер.
    const topicLength = (topic || '').length;
    const topicHelperText = topicLength > TOPIC_LONG_AT
        ? `${topicLength} characters — the PDF keeps the full text, but the email subject and file name will be shortened`
        : `${topicLength} characters`;

    function isFormValid() {
        if (!topic.trim()) return false;
        if (!participants || participants.length === 0) return false;
        if (!participants.every(e => e.signature)) return false;
        if (!givenBy || !givenBy.employeeId) return false;
        if (!date) return false;
        return true;
    }

    const currentSigningName = (() => {
        if (selectedParticipantIndex === null) return 'Unknown';
        const p = participants[selectedParticipantIndex];
        return p?.FullName || 'Unknown';
    })();

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Paper sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate('/')} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>Safety Stand Down / Coaching</Typography>
                    {isSending && <CircularProgress size={20} />}
                </Stack>
            </Paper>

            <Box sx={{ p: 1.5, flexGrow: 1 }}>
                {errorMsg && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

                <Paper sx={{ p: 1.5 }}>
                    {/* Type, Date, Given By */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                            <ToggleButtonGroup
                                color="primary"
                                value={type}
                                exclusive
                                onChange={(e, newType) => {
                                    if (newType !== null) {
                                        setType(newType);
                                        saveRecordAndReload({ newStatus: STATUS_PROCESS, newType });
                                    }
                                }}
                                size="small"
                                fullWidth
                                sx={{ height: '40px' }}
                            >
                                <ToggleButton value="safety_stand_down" sx={{ px: 0.5, py: 0.5, fontSize: '0.65rem', textTransform: 'none' }}>
                                    <GppGoodIcon sx={{ mr: 0.3, fontSize: '1rem' }} /> Safety Stand Down
                                </ToggleButton>
                                <ToggleButton value="coaching" sx={{ px: 0.5, py: 0.5, fontSize: '0.65rem', textTransform: 'none' }}>
                                    <SchoolIcon sx={{ mr: 0.3, fontSize: '1rem' }} /> Coaching
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Date"
                                type="date"
                                value={date}
                                onChange={handleDateChange}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Given By"
                                value={givenBy?.FullName || ''}
                                size="small"
                                fullWidth
                                onClick={() => setOpenGivenByModal(true)}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setOpenGivenByModal(true)}>
                                                <PersonSearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: { cursor: 'pointer' }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Topic with dictation */}
                    <TextField
                        label="TOPIC"
                        value={topic}
                        onChange={handleTopicChange}
                        onBlur={handleTopicBlur}
                        size="small"
                        fullWidth
                        multiline
                        // Надиктованный абзац должен быть виден целиком, а не в окошке
                        // на две строки — иначе непонятно, что вообще записалось.
                        minRows={2}
                        maxRows={10}
                        required
                        placeholder="Enter topic"
                        helperText={topicHelperText}
                        FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: speechSupported && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isListening) stopTopicDictation();
                                            else startTopicDictation();
                                        }}
                                        color={isListening ? "error" : "default"}
                                    >
                                        {isListening ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Participants Section */}
                    <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        PARTICIPANTS
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleOpenSelectModal}
                        size="small"
                        sx={{ mb: 1 }}
                        fullWidth
                    >
                        Select Participants
                    </Button>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Signature</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Signed At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {participants.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                            <Typography variant="caption" color="text.secondary">No participants selected</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    participants.map((p, idx) => (
                                        <TableRow key={`${p._type}-${p.FullName}-${idx}`}>
                                            <TableCell sx={{ fontSize: '0.8rem' }}>{p.FullName}</TableCell>
                                            <TableCell>
                                                {p.signature ? (
                                                    <img
                                                        src={p.signature}
                                                        alt="Signature"
                                                        style={{ width: '80px', height: '30px', border: '1px solid #ccc' }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Not signed</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                {p.signedAt ? new Date(p.signedAt).toLocaleString() : '---'}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleOpenSignModal(idx)}
                                                    >
                                                        {p.signature ? 'Re-Sign' : 'Sign'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleDeleteParticipant(idx)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Submit Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSend}
                            disabled={!isFormValid() || isSending}
                            size="medium"
                            sx={{ minWidth: '200px' }}
                        >
                            {isSending ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                            {isSending ? 'Sending...' : 'Submit Form'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
            {/* Participants Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={openSelectModal}
                onClose={() => setOpenSelectModal(false)}
                onSelect={handleSaveParticipants}
                alreadySelected={participants}
            />

            {/* Given By Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={openGivenByModal}
                onClose={() => setOpenGivenByModal(false)}
                onSelect={handleSaveGivenBy}
                alreadySelected={givenBy ? [givenBy] : []}
                singleSelect={true}
            />
            <SignatureModalWithAuto
                open={openSignModal}
                onClose={() => setOpenSignModal(false)}
                sigPadRef={sigPadRef}
                employeeName={currentSigningName}
                onAccept={handleAcceptSignature}
                existingSignature={selectedParticipantIndex !== null ? participants[selectedParticipantIndex]?.signature : null}
            />
        </Box>
    );
}
